import React, { useState } from "react";
import { Upload, message, Modal } from "antd";
import type { UploadProps } from "antd";
import { Storage } from "aws-amplify";
import styles from "../JobLead.module.scss";
import UploadIcon from "../../../../../assets/images/upload-icon.svg";
import pdfIcon from "../../../../../assets/images/view_pdf.svg";
import jpgIcon from "../../../../../assets/images/jpeg-image-icon.svg";
import pngIcon from "../../../../../assets/images/png-image-icon.svg";
import excelIcon from "../../../../../assets/images/excel-file-icon.svg";
import csvIcon from "../../../../../assets/images/csvIcon.svg";
import docIcon from "../../../../../assets/images/docIcon.svg";
import CreateJobServices from "../../../../../services/createJob.services";
import { fileUploadProps } from "./constants";
import { FileTypes } from "../../../../../enums/FileUploadEnums";
import { UploadMessages } from "../../../../../enums/UploadDraggerMessagesEnums";
import { mimeTypeLabels } from "../../../../../constants/MiscKeys";

const iconMapping = {
  [FileTypes.PDF]: pdfIcon,
  [FileTypes.CSV]: csvIcon,
  [FileTypes.XLS]: excelIcon,
  [FileTypes.XLSX]: excelIcon,
  [FileTypes.DOC]: docIcon,
  [FileTypes.DOCX]: docIcon,
  [FileTypes.PNG]: pngIcon,
  [FileTypes.JPEG]: jpgIcon,
  [FileTypes.JPG]: jpgIcon,
};

const ProjectInformationSheetProofUpload = ({
  onFilesUploaded,
  docType,
  filesUploaded,
  maxFileSize,
  allowedFileTypes,
  leadId,
}) => {
  const { DeleteDocuments } = CreateJobServices();
  const { mutateAsync: deleteDocuments } = DeleteDocuments();
  const { Dragger } = Upload;
  const {
    maxFileCount,
    defaultSheetStatus,
    fileUploadSuccessMessage,
    signatureType,
  } = fileUploadProps;

  const [fileList, setFileList] = useState(filesUploaded || []);
  const handleUpload = async (file, onSuccess, onError, onProgress) => {
    try {
      const timestampMs = new Date().getTime();
      const fileName = `${timestampMs}/${file.name}`;
      const result = await Storage.put(fileName, file, {
        contentType: file.type,
        progressCallback(progress) {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          onProgress({ percent }, file);
        },
      });
      const fileURL = await Storage.get(result.key, { level: "public" });

      const newFile = {
        name: file.name,
        docAttachment: fileURL?.split("?")[0], // Remove query params
        docAttachmentId: file.uid,
        fileType: file.type,
        sheetStatus: defaultSheetStatus,
        docType: docType,
        document: docType,
        signatureVerified: false,
      };
      message.success(`${file.name} uploaded successfully`);
      onSuccess(fileUploadSuccessMessage);
      onFilesUploaded(newFile);
    } catch (error) {
      console.error("Upload failed:", error);
      message.error(`${file.name} upload failed.`);
      onError(error);
    }
  };

  const maxCount = maxFileCount ?? 1;

  const draggerProps: UploadProps = {
    fileList,
    maxCount,
    headers: { authorization: "authorization-text" },

    beforeUpload: (file) => {
      const isDuplicate = fileList.some(
        (existing) => existing.name === file.name
      );
      if (isDuplicate) {
        Modal.confirm({
          title: "Duplicate File",
          content: `${file.name} is already uploaded. Upload again?`,
          onOk: () => {
            draggerProps.customRequest?.({
              file,
              onSuccess: () =>
                message.success(`${file.name} uploaded successfully`),
              onError: () => message.error(`${file.name} upload failed`),
              onProgress: (progress) => {},
            });
          },
        });
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= maxCount) {
        message.error(`You can only upload up to ${maxCount} files.`);
        return Upload.LIST_IGNORE;
      }

      if (allowedFileTypes && allowedFileTypes.length > 0) {
        if (!allowedFileTypes.includes(file.type)) {
          const readableTypes = allowedFileTypes
            .map((t) => mimeTypeLabels[t] || t)
            .join(", ");
          message.error(
            `Invalid file type. Only ${readableTypes} files are allowed.`
          );
          return Upload.LIST_IGNORE;
        }
      } else if (file.type !== FileTypes.PDF) {
        message.error("Only PDF files are allowed.");
        return Upload.LIST_IGNORE;
      }

      if (file.size > maxFileSize * 1024 * 1024) {
        message.error(UploadMessages.LESS_THAN_10_MB);
        return Upload.LIST_IGNORE;
      }
      return true;
    },

    customRequest: ({ file, onSuccess, onError, onProgress }) =>
      handleUpload(file, onSuccess, onError, onProgress),
    onChange(info) {
      setFileList(info.fileList);
    },
    onRemove: async (file) => {
      setFileList([]);
      onFilesUploaded(null);
    },

    iconRender: (file) => {
      const iconSrc = iconMapping[file?.type ?? file?.fileType] || pdfIcon;
      return (
        <img
          src={iconSrc}
          alt={file.name}
          style={{ width: 35, height: 35, marginRight: 8, marginTop: 8 }}
        />
      );
    },

    progress: { style: { marginLeft: 16 }, strokeColor: fileUploadProps.PROGRESS_COLOR },
  };

  const getHintText = () => {
    if (docType === signatureType)
      return ", Only PNG and JPG files are allowed";

    if (allowedFileTypes?.length) {
      const readableTypes = allowedFileTypes
        .map((t) => mimeTypeLabels[t] || "")
        .join(", ");
      return `, Only ${readableTypes} files are allowed`;
    }

    return ""; // Default or PDF implication
  };

  return (
    <div>
      <Dragger {...draggerProps}>
        <p className={styles.UploadIconContainer}>
          <img src={UploadIcon} alt="upload icon" />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint">Max file size is {maxFileSize}MB{getHintText()}</p>
      </Dragger>
    </div>
  );
};

export default ProjectInformationSheetProofUpload;
